import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Check, RefreshCw, AlertTriangle, Link2, Wand2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

const CONFIDENCE_LABEL = {
  strong: 'Multi-source agreement',
  enrollments: 'Inferred from enrollments',
  registrations: 'Inferred from registrations',
  parent_ids: 'Inferred from secondary parent_ids',
  audit: 'Inferred from merge audit log',
};

const CONFIDENCE_COLOR = {
  strong: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  enrollments: 'bg-sky-100 text-sky-800 border-sky-200',
  registrations: 'bg-violet-100 text-violet-800 border-violet-200',
  parent_ids: 'bg-amber-100 text-amber-800 border-amber-200',
  audit: 'bg-slate-200 text-slate-700 border-slate-300',
};

export const OrphanPlayersTab = ({ loading, orphans, onLinkParent, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run result
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.post('/admin/duplicates/repair-orphan-player-links');
      setPreview(r.data);
      if ((r.data?.repaired || 0) === 0) {
        toast.info('Nothing auto-repairable right now.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run repair preview');
    } finally {
      setBusy(false);
    }
  };

  const applyRepair = async () => {
    setApplying(true);
    try {
      const r = await api.post('/admin/duplicates/repair-orphan-player-links?apply=true');
      toast.success(r.data?.message || 'Repair applied');
      setPreview(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply repair');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
        <p className="text-slate-500 mt-2">Scanning for orphans...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Auto-Repair Banner — always visible so admins can also re-link
          children whose parent_id silently points at a deleted user but
          aren't surfaced in the orphan list. */}
      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-emerald-100 p-2">
                <Wand2 className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="font-semibold text-emerald-900">Auto-Repair Player ↔ Parent Links</p>
                <p className="text-sm text-emerald-800/80">
                  Cross-references enrollments, tournament/camp registrations, parent_ids array,
                  and the parent-merge audit log to reconstruct missing or broken{' '}
                  <code className="bg-white px-1 rounded text-[11px]">parent_id</code> links.
                  Always previews before applying.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={runPreview}
                disabled={busy || applying}
                data-testid="repair-orphan-links-preview-btn"
              >
                {busy ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Scanning…</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" />Run Auto-Repair (Preview)</>
                )}
              </Button>
              {preview && (preview.repaired || 0) > 0 && (
                <Button
                  onClick={applyRepair}
                  disabled={applying}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="repair-orphan-links-apply-btn"
                >
                  {applying ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Applying…</>
                  ) : (
                    <>Apply Repair to {preview.repaired} player{preview.repaired === 1 ? '' : 's'}</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {preview && (
            <div className="rounded-md border border-emerald-200 bg-white p-3 space-y-2"
                 data-testid="repair-orphan-links-preview">
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <span><b>{preview.total_players}</b> total players</span>
                <span>·</span>
                <span><b>{preview.needs_repair}</b> need repair</span>
                <span>·</span>
                <span className="text-emerald-700"><b>{preview.repaired}</b> auto-resolvable</span>
                {preview.skipped_no_signal > 0 && (
                  <>
                    <span>·</span>
                    <span className="text-amber-700">
                      <b>{preview.skipped_no_signal}</b> need manual link
                    </span>
                  </>
                )}
              </div>
              {Object.keys(preview.confidence_breakdown || {}).length > 0 && (
                <div className="flex gap-2 flex-wrap text-xs">
                  {Object.entries(preview.confidence_breakdown).map(([k, n]) => (
                    <span key={k} className={`px-2 py-0.5 rounded border ${CONFIDENCE_COLOR[k] || ''}`}>
                      {CONFIDENCE_LABEL[k] || k}: {n}
                    </span>
                  ))}
                </div>
              )}
              {(preview.samples || []).length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-700 hover:text-slate-900">
                    Show plan ({preview.samples.length} sample{preview.samples.length === 1 ? '' : 's'})
                  </summary>
                  <div className="mt-2 space-y-1 max-h-60 overflow-y-auto font-mono">
                    {preview.samples.map((s, i) => (
                      <div key={i} className="border-b last:border-0 py-1">
                        <span className="font-sans font-medium">{s.name || '(no name)'}</span>
                        {' → parent '}
                        <span className="text-slate-500">{s.new_parent_id?.slice(0, 8)}…</span>
                        <span className={`ml-2 inline-block px-1.5 py-0 rounded text-[10px] border ${CONFIDENCE_COLOR[s.confidence] || ''} font-sans`}>
                          {s.confidence}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual / per-row link UI */}
      {orphans.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Check className="h-12 w-12 mx-auto text-green-500 mb-3" />
            <p className="text-lg font-medium text-green-700">No orphan players found</p>
            <p className="text-slate-500">Every child record is linked to at least one valid parent/customer account.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b bg-amber-50/50 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              {orphans.length} player(s) have broken or missing parent links. Run the auto-repair above
              first; anything left over can be manually linked here.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="orphans-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-2">Player</th>
                    <th className="text-left px-4 py-2">ID</th>
                    <th className="text-left px-4 py-2">DOB</th>
                    <th className="text-left px-4 py-2">Issue</th>
                    <th className="text-left px-4 py-2">Broken References</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map((o) => (
                    <tr key={o.id} className="border-t hover:bg-slate-50" data-testid={`orphan-row-${o.id}`}>
                      <td className="px-4 py-2 font-medium">{o.name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500">
                        {o.numeric_id || o.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-2 text-slate-600">{o.date_of_birth || '—'}</td>
                      <td className="px-4 py-2">
                        <Badge variant={o.reason === 'no_parent_ref' ? 'destructive' : 'secondary'}>
                          {o.reason === 'no_parent_ref' ? 'No parent' : 'Broken ref'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 font-mono">
                        {o.broken_refs.length === 0
                          ? '—'
                          : o.broken_refs
                              .map((r) => `${r.field}: ${r.parent_id.slice(0, 8)}…`)
                              .join(', ')}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onLinkParent(o)}
                          data-testid={`link-parent-btn-${o.id}`}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          Link Parent
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
